import React from 'react'
import styled from '@emotion/styled'

import {Accordion} from '@vio/atlas/Accordion'
import {Card} from '@vio/atlas/Card'
import {Box} from '@vio/atlas/helpers/Box'
import {IconName} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/src/themes'
import {Text} from '@vio/atlas/Text'

const StyledText = styled(Text)`
  padding-bottom: ${cssTheme.layout.spacing.s400};
`

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${cssTheme.layout.spacing.s800};
`

/** `label` is the analytics id of a question. SAPI FAQs have one, SEO FAQs don't. */
export type FAQItem = {question: string; answer: string; label?: string}

export type FAQToggleHandler = (faq: FAQItem, isOpen: boolean) => void

const getFAQKey = (faq: FAQItem) => faq.label ?? faq.question

type FAQAccordionProps = {
  faq: FAQItem
  isLast: boolean
  icon?: IconName
  onToggle?: FAQToggleHandler
  isMobile?: boolean
}

const FAQAccordion = ({
  faq,
  isLast,
  icon,
  onToggle,
  isMobile
}: FAQAccordionProps) => (
  <Box>
    <Accordion
      header={<bdi>{faq.question}</bdi>}
      icon={icon}
      size="md"
      onToggle={isOpen => onToggle?.(faq, isOpen)}
      withDivider={!isLast}
      sidePaddings={isMobile ? 's400' : undefined}
    >
      <StyledText dir="auto">{faq.answer}</StyledText>
    </Accordion>
  </Box>
)

/**
 * Question and answer accordions in a card: a single column on mobile, two on
 * desktop. Section chrome (title, dividers, tracking) belongs to the caller,
 * which also decides the layout so the same list can be reused off the AP.
 */
export const FAQs = ({
  faqs,
  isMobile = false,
  icon,
  onToggle
}: {
  faqs: FAQItem[]
  isMobile?: boolean
  /** Leading icon on every question. Questions render without one when omitted. */
  icon?: IconName
  onToggle?: FAQToggleHandler
}) => {
  const desktopColumnSplitIndex = Math.ceil(faqs.length / 2)
  const desktopLeftColumnFaqs = faqs.slice(0, desktopColumnSplitIndex)
  const desktopRightColumnFaqs = faqs.slice(desktopColumnSplitIndex)

  return (
    <Card
      borderRadius={isMobile ? 'md' : 'none'}
      borderColorPath={isMobile ? 'border.neutral.secondary' : 'transparent'}
    >
      {isMobile ? (
        faqs.map((faq, i) => (
          <FAQAccordion
            key={getFAQKey(faq)}
            faq={faq}
            isLast={i === faqs.length - 1}
            icon={icon}
            onToggle={onToggle}
            isMobile
          />
        ))
      ) : (
        <Wrapper>
          <div>
            {desktopLeftColumnFaqs.map((faq, i) => (
              <FAQAccordion
                key={getFAQKey(faq)}
                faq={faq}
                isLast={i === desktopLeftColumnFaqs.length - 1}
                icon={icon}
                onToggle={onToggle}
              />
            ))}
          </div>
          <div>
            {desktopRightColumnFaqs.map((faq, i) => (
              <FAQAccordion
                key={getFAQKey(faq)}
                faq={faq}
                isLast={i === desktopRightColumnFaqs.length - 1}
                icon={icon}
                onToggle={onToggle}
              />
            ))}
          </div>
        </Wrapper>
      )}
    </Card>
  )
}
